import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { LoadingSections, OverlayComponent, SharedStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const initialState: SharedStateType = {
  loading: false,
  notification: null,
  modal: undefined,
  overlay: undefined,
  navigateURL: "",
  loadingSections: {},
  loadingTypes: [],
  user: null,
  teams: [],
  projects: [],
  roles: [],
  users: [],
  usersTotal: 0,
};

const reducer = createReducer<SharedStateType, Action>(initialState)
  .handleAction(actions.getUsers.success, (state, action) =>
    produce(state, (nextState) => {
      const { rows, count, is_clear } = action.payload;
      nextState.users = !is_clear ? [...nextState.users, ...rows] : [...rows];
      nextState.usersTotal = count;
    }),
  )
  .handleAction(actions.startLoading, (state) =>
    produce(state, (nextState) => {
      nextState.loading = true;
    }),
  )
  .handleAction(actions.stopLoading, (state) =>
    produce(state, (nextState) => {
      nextState.loading = false;
    }),
  )
  .handleAction(actions.showNotification, (state, action) =>
    produce(state, (nexState) => {
      nexState.notification = action.payload;
    }),
  )
  .handleAction(actions.showModal, (state, action) =>
    produce(state, (nexState) => {
      nexState.modal = action.payload;
    }),
  )
  .handleAction(actions.hideModal, (state) =>
    produce(state, (nexState) => {
      nexState.modal = undefined;
    }),
  )
  .handleAction(actions.updateOverlay, (state, action) =>
    produce(state, (nexState) => {
      if (state.overlay) {
        nexState.overlay = { ...nexState.overlay, ...action.payload } as OverlayComponent;
      }
    }),
  )
  .handleAction(actions.showOverlay, (state, action) =>
    produce(state, (nexState) => {
      nexState.overlay = action.payload;
    }),
  )
  .handleAction(actions.getTeams.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.teams = action.payload;
    }),
  )
  .handleAction(actions.getProjects.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.projects = action.payload.rows;
    }),
  )
  .handleAction(actions.getRoles.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.roles = action.payload;
    }),
  )
  .handleAction(actions.hideOverlay, (state) =>
    produce(state, (nexState) => {
      nexState.overlay = undefined;
    }),
  )

  .handleAction(actions.addLoadingType, (state, action) =>
    produce(state, (nextState) => {
      nextState.loadingTypes = [...state.loadingTypes, action.payload];
    }),
  )
  .handleAction(actions.removeLoadingType, (state, action) =>
    produce(state, (nextState) => {
      const foundIndex = nextState.loadingTypes.findIndex(({ name }) => name === action.payload);

      if (foundIndex !== -1) {
        nextState.loadingTypes.splice(foundIndex, 1);
      }

      const hasRemainingEntry = nextState.loadingTypes.some(({ name }) => name === action.payload);

      if (!hasRemainingEntry) {
        delete nextState.loadingSections[action.payload];
      }
    }),
  )
  .handleAction(actions.addLoadingSection, (state, action) =>
    produce(state, (nextState) => {
      const loadingSections: LoadingSections = { ...state.loadingSections };

      if (!loadingSections[action.payload.loadingSection]) {
        loadingSections[action.payload.loadingSection] = [];
      }

      loadingSections[action.payload.loadingSection] = [
        ...Array.from(new Set([...loadingSections[action.payload.loadingSection], action.payload.requestName])),
      ];

      nextState.loadingSections = loadingSections;
    }),
  )
  .handleAction(actions.removeLoadingSection, (state, action) =>
    produce(state, (nextState) => {
      const loadingSections = { ...state.loadingSections };

      if (loadingSections[action.payload.loadingSection]) {
        loadingSections[action.payload.loadingSection] = loadingSections[action.payload.loadingSection].filter(
          (loadingRequest) => loadingRequest !== action.payload.requestName,
        );

        if (!loadingSections[action.payload.loadingSection].length) {
          delete loadingSections[action.payload.loadingSection];
        }

        nextState.loadingSections = loadingSections;
      }
    }),
  )
  .handleAction(actions.getUserDetails.success, (state, action) =>
    produce(state, (nexState) => {
      nexState.user = action.payload;
    }),
  )
  .handleAction(actions.navigate, (state, action) =>
    produce(state, (nexState) => {
      nexState.navigateURL = action.payload;
    }),
  );

export { reducer as SharedReducer };
